import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/useAuth.js";
import { Icon, colors } from "../ui/index.js";
const navGroups = [
    {
        label: "Booking",
        items: [
            { to: "/", label: "Dashboard", iconName: "layout-dashboard", end: true },
            { to: "/sessions", label: "Sessions", iconName: "calendar" },
        ],
    },
    {
        label: "Manage",
        items: [
            { to: "/rules", label: "Booking Preferences", iconName: "book-open" },
            { to: "/credentials", label: "Credentials", iconName: "key" },
        ],
    },
    {
        label: "Admin",
        items: [
            { to: "/audit", label: "Audit", iconName: "file-text" },
            { to: "/settings", label: "Settings", iconName: "settings" },
        ],
    },
];
const SIDEBAR_EXPANDED = 220;
const SIDEBAR_COLLAPSED = 56;
const RESPONSIVE_CSS = `
  .shell-root {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: ${colors.surfaceSecondary};
  }
  .shell-body {
    flex: 1;
    display: flex;
    overflow: hidden;
    min-height: 0;
  }
  .shell-main {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
  }
  .shell-hamburger { display: flex; }
  .shell-main-inner {
    transition: max-width 0.25s ease, padding 0.25s ease;
  }
  @media (min-width: 768px) {
    .shell-sidebar { display: flex !important; }
    .shell-hamburger { display: none !important; }
    .shell-main-inner {
      max-width: 720px !important;
      padding-bottom: 32px !important;
    }
  }
  @media (min-width: 1024px) {
    .shell-main-inner {
      max-width: 860px !important;
      padding: 24px 32px !important;
    }
  }
  @media (max-width: 767px) {
    .shell-sidebar { display: none !important; }
  }
  @keyframes drawer-slide {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
  @keyframes drawer-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes drawer-slide-out {
    from { transform: translateX(0); }
    to { transform: translateX(-100%); }
  }
  @keyframes drawer-fade-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;
const STORAGE_KEY = "sidecar-sidebar-collapsed";
// Per-route document titles. Exact matches first; detail routes fall through to
// the prefix checks in titleForPath().
const TITLE_BY_PATH = {
    "/": "Dashboard",
    "/sessions": "Sessions",
    "/rules": "Booking Preferences",
    "/credentials": "Credentials",
    "/audit": "Audit",
    "/settings": "Settings",
};
function titleForPath(pathname) {
    const exact = TITLE_BY_PATH[pathname];
    if (exact)
        return `${exact} · Booking Bot`;
    // Detail routes: /sessions/:id, /rules/:id
    if (pathname.startsWith("/sessions/"))
        return "Session · Booking Bot";
    if (pathname.startsWith("/rules/"))
        return "Booking preference · Booking Bot";
    return "Booking Bot";
}
export function Shell({ children }) {
    const { logout, user } = useAuth();
    const username = user?.name ?? user?.email ?? null;
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
    const closeDrawer = useCallback(() => setDrawerOpen(false), []);
    function toggleCollapsed() {
        setCollapsed((prev) => {
            localStorage.setItem(STORAGE_KEY, prev ? "0" : "1");
            return !prev;
        });
    }
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const handler = (e) => {
            if (e.matches)
                setDrawerOpen(false);
        };
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    useEffect(() => {
        const meta = document.querySelector("meta[name=\"theme-color\"]");
        if (meta)
            meta.setAttribute("content", colors.primary);
    }, []);
    useEffect(() => {
        document.title = titleForPath(location.pathname);
    }, [location.pathname]);
    const impersonating = user?.impersonating === true;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: RESPONSIVE_CSS }), _jsxs("div", { className: "shell-root", children: [impersonating && (_jsxs("div", { role: "status", style: {
                            background: colors.warningDark,
                            color: colors.warningLight,
                            padding: "6px 16px",
                            fontSize: "0.78rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexShrink: 0,
                            zIndex: 11,
                        }, children: [_jsxs("span", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [_jsx(Icon, { name: "alert-triangle", size: 14, strokeWidth: 2.2 }), _jsx("strong", { children: "Admin impersonation" }), _jsxs("span", { style: { opacity: 0.85 }, children: ["\u2014 viewing as ", _jsx("strong", { children: user?.email })] })] }), _jsx("button", { type: "button", onClick: () => void logout(), style: {
                                    background: "transparent",
                                    color: colors.warningLight,
                                    border: `1px solid ${colors.warningLight}`,
                                    padding: "3px 10px",
                                    borderRadius: 4,
                                    fontSize: "0.74rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }, children: "End impersonation" })] })), _jsxs("header", { style: {
                            background: colors.primary,
                            color: colors.surface,
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            zIndex: 10,
                        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, flex: 1 }, children: [_jsx("button", { className: "shell-hamburger", onClick: () => setDrawerOpen(true), style: {
                                            background: "none",
                                            border: "none",
                                            color: colors.surface,
                                            cursor: "pointer",
                                            padding: 4,
                                            marginLeft: -4,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }, "aria-label": "Open menu", children: _jsx(Icon, { name: "menu", size: 22, strokeWidth: 2 }) }), _jsx(Icon, { name: "zap", size: 20, strokeWidth: 2, style: { marginRight: 2 } }), _jsx("h1", { style: { margin: 0, fontSize: "1.1rem", fontWeight: 600 }, children: "Booking Bot" })] }), username && (_jsx("span", { style: { fontSize: "0.82rem", opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", maxWidth: 180, textOverflow: "ellipsis" }, children: username }))] }), _jsxs("div", { className: "shell-body", children: [_jsxs("aside", { className: "shell-sidebar", style: {
                                    display: "none",
                                    flexDirection: "column",
                                    width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED,
                                    background: colors.surface,
                                    borderRight: `1px solid ${colors.border}`,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    transition: "width 0.25s ease",
                                }, children: [_jsx("div", { style: {
                                            display: "flex",
                                            justifyContent: collapsed ? "center" : "flex-end",
                                            padding: "6px 8px 2px",
                                            flexShrink: 0,
                                        }, children: _jsx("button", { onClick: toggleCollapsed, title: collapsed ? "Expand sidebar" : "Collapse sidebar", style: {
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 28,
                                                height: 28,
                                                borderRadius: 6,
                                                background: "none",
                                                border: `1px solid ${colors.border}`,
                                                cursor: "pointer",
                                                color: colors.textTertiary,
                                                transition: "background 0.15s",
                                            }, children: _jsx(Icon, { name: collapsed ? "chevron-right" : "chevron-left", size: 14, strokeWidth: 2 }) }) }), _jsx(SidebarContent, { collapsed: collapsed, onLogout: logout, username: username })] }), _jsx("main", { className: "shell-main", children: _jsx("div", { className: "shell-main-inner", style: {
                                        maxWidth: 600,
                                        margin: "0 auto",
                                        padding: "16px",
                                        paddingBottom: 16,
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }, children: children }) })] }), drawerOpen && (_jsx(MobileDrawer, { onClose: closeDrawer, onLogout: logout, username: username }))] })] }));
}
/* ── Shared sidebar content ──────────────────────────────────────────────── */
function SidebarContent({ collapsed = false, onLogout, username, }) {
    return (_jsxs(_Fragment, { children: [_jsx("nav", { style: { flex: 1, padding: "4px 0", overflowY: "auto" }, children: navGroups.map((group, gi) => (_jsxs("div", { children: [group.label && !collapsed && (_jsx("div", { style: {
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                color: colors.textTertiary,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                padding: gi === 0 ? "8px 20px 4px" : "14px 20px 4px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            }, children: group.label })), collapsed && gi > 0 && (_jsx("div", { style: { margin: "6px 12px", borderTop: `1px solid ${colors.border}` } })), group.items.map((item) => (_jsx(SidebarLink, { item: item, collapsed: collapsed }, item.to)))] }, gi))) }), _jsxs("div", { style: { borderTop: `1px solid ${colors.border}`, padding: "4px 0", flexShrink: 0 }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: collapsed ? "center" : "flex-start",
                            gap: 10,
                            padding: collapsed ? "9px 0" : "9px 20px",
                            fontSize: "0.88rem",
                            color: colors.textSecondary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                        }, children: [_jsx(Icon, { name: "user", size: 18, strokeWidth: 1.8, style: { flexShrink: 0 } }), !collapsed && (_jsx("span", { style: { overflow: "hidden", textOverflow: "ellipsis" }, children: username ?? "Account" }))] }), _jsxs("button", { onClick: onLogout, title: collapsed ? "Logout" : undefined, style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: collapsed ? "center" : "flex-start",
                            gap: 10,
                            padding: collapsed ? "9px 0" : "9px 20px",
                            width: "100%",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "0.88rem",
                            color: colors.danger,
                        }, children: [_jsx(Icon, { name: "log-out", size: 18, strokeWidth: 1.8, style: { flexShrink: 0 } }), !collapsed && _jsx("span", { style: { whiteSpace: "nowrap" }, children: "Logout" })] })] })] }));
}
function SidebarLink({ item, collapsed = false }) {
    return (_jsxs(NavLink, { to: item.to, end: item.end, title: collapsed ? item.label : undefined, style: ({ isActive }) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 10,
            padding: collapsed ? "9px 0" : "9px 20px",
            textDecoration: "none",
            fontSize: "0.88rem",
            color: isActive ? colors.primary : colors.textSecondary,
            fontWeight: isActive ? 600 : 400,
            background: isActive ? colors.primaryLight : "transparent",
            borderRight: isActive ? `3px solid ${colors.primary}` : "3px solid transparent",
            transition: "background 0.15s, padding 0.25s ease",
            whiteSpace: "nowrap",
            overflow: "hidden",
        }), children: [_jsx(Icon, { name: item.iconName, size: 18, strokeWidth: 1.8, style: { flexShrink: 0 } }), !collapsed && item.label] }));
}
/* ── Mobile Drawer ──────────────────────────────────────────────────────── */
function MobileDrawer({ onClose, onLogout, username, }) {
    const [closing, setClosing] = useState(false);
    const animateClose = useCallback(() => {
        setClosing(true);
        setTimeout(onClose, 200);
    }, [onClose]);
    useEffect(() => {
        let startX = 0;
        function onTouchStart(e) { startX = e.touches[0].clientX; }
        function onTouchEnd(e) {
            const dx = e.changedTouches[0].clientX - startX;
            if (dx < -60)
                animateClose();
        }
        document.addEventListener("touchstart", onTouchStart);
        document.addEventListener("touchend", onTouchEnd);
        return () => {
            document.removeEventListener("touchstart", onTouchStart);
            document.removeEventListener("touchend", onTouchEnd);
        };
    }, [animateClose]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { onClick: animateClose, style: {
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 50,
                    animation: `${closing ? "drawer-fade-out" : "drawer-fade"} 0.2s ease-out forwards`,
                } }), _jsxs("div", { style: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: 280,
                    maxWidth: "80vw",
                    background: colors.surface,
                    zIndex: 51,
                    display: "flex",
                    flexDirection: "column",
                    animation: `${closing ? "drawer-slide-out" : "drawer-slide"} 0.2s ease-out forwards`,
                    boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
                }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "14px 20px",
                            borderBottom: `1px solid ${colors.border}`,
                            background: colors.primary,
                        }, children: [_jsx(Icon, { name: "zap", size: 18, strokeWidth: 2, style: { color: colors.surface } }), _jsx("span", { style: { fontSize: "1rem", fontWeight: 600, color: colors.surface }, children: "Booking Bot" })] }), _jsx(SidebarContent, { onLogout: () => {
                            animateClose();
                            onLogout();
                        }, username: username })] })] }));
}
